import { forwardRef } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { useShapes } from "@/canvas/components/BackgroundShapes/ShapesContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";

interface AuctionRateCardSlots {
  image?: React.ReactNode;
  bidBadge?: React.ReactNode;
  title: React.ReactNode;
  detailsChevron?: React.ReactNode;
  price: React.ReactNode;
  priceCaption?: React.ReactNode;
  hotItemBadge?: React.ReactNode;
  callToAction: React.ReactNode;
  outbidHelper?: React.ReactNode;
  detailsLink?: React.ReactNode;
  description?: React.ReactNode;
  autoBidLabel?: React.ReactNode;
  antiSnipingInfo?: React.ReactNode;
}

interface AuctionRateCardLayoutProps {
  id: string;
  isSoldOut?: boolean;
  hasBrandBorder?: boolean;
  onClick?: () => void;
  slots: AuctionRateCardSlots;
}

export const AuctionRateCardLayout = forwardRef<HTMLDivElement, AuctionRateCardLayoutProps>(
  ({ id, isSoldOut = false, hasBrandBorder = false, onClick, slots }, ref) => {
    const { isSmallScreen } = useMediaQuery();
    const { backgroundColor } = useShapes();

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <Box
        ref={ref}
        id={id}
        borderRadius={1}
        padding={{ xs: 1.5, md: 3 }}
        border={(theme) =>
          `1px solid ${hasBrandBorder ? theme.palette.forms.border.brand : theme.palette.formsComponents.divider.borderColor}`
        }
        sx={(theme) => ({
          opacity: isSoldOut ? 0.4 : undefined,
          cursor: onClick ? "pointer" : undefined,
          ...(hasBrandBorder
            ? { backgroundColor: theme.palette.forms.surface.supershy }
            : { backgroundColor: isSmallScreen ? "transparent" : backgroundColor }),
        })}
        onClick={onClick}
        data-test="auction-item-block-card"
      >
        {isSmallScreen ? <MobileLayout slots={slots} /> : <DesktopLayout slots={slots} />}
      </Box>
    );
  }
);

AuctionRateCardLayout.displayName = "AuctionRateCardLayout";

const DesktopLayout = ({ slots }: { slots: AuctionRateCardSlots }) => (
  <Stack direction="row" alignItems="flex-start" gap={3}>
    {slots.image && <Box flexShrink={0}>{slots.image}</Box>}

    <Stack gap={1} flex={1} minWidth={0}>
      {slots.bidBadge}

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box flex={1} minWidth={0}>
          {slots.title}
        </Box>
        {slots.detailsChevron}
      </Stack>

      <Stack direction="row" rowGap={0.5} columnGap={1} alignItems="center" flexWrap="wrap">
        {slots.price}
        {slots.priceCaption}
        {slots.hotItemBadge}
      </Stack>

      <Stack direction="row" gap={1.5} alignItems="center">
        {slots.callToAction}
        {slots.autoBidLabel}
      </Stack>

      {slots.outbidHelper}

      {slots.antiSnipingInfo}

      {slots.description && <Box marginBlockStart={1}>{slots.description}</Box>}
    </Stack>
  </Stack>
);

const MobileLayout = ({ slots }: { slots: AuctionRateCardSlots }) => (
  <Stack gap={1.5}>
    {slots.image && <Box>{slots.image}</Box>}

    <Stack gap={1}>
      {slots.bidBadge}
      {slots.title}

      <Stack direction="row" rowGap={0.5} columnGap={1} alignItems="center" flexWrap="wrap">
        {slots.price}
        {slots.priceCaption}
        {slots.hotItemBadge}
      </Stack>

      {slots.description && <Box>{slots.description}</Box>}
    </Stack>

    <Stack direction="row" justifyContent="space-between" alignItems="center">
      {slots.callToAction}
      {slots.detailsLink}
    </Stack>

    {slots.outbidHelper}

    {slots.autoBidLabel}
    {slots.antiSnipingInfo}
  </Stack>
);
